// What a person can CREATE, declared once — with the pane it opens and who may.
//
// # The defect this is built to not repeat
//
// `destinations.ts` exists because the rail, the command palette and the reachability
// test were three lists, which is how a screen gets added to one and not the others. The
// create menu is the same shape one level along: a `+` in the topbar, the palette, and
// whatever comes next all have to offer the same set, and the moment there are two
// arrays somebody adds "yeni harcama talebi" to the menu and not to the palette.
//
// So this is ONE declaration and adding a creation is ONE LINE here. Not a component,
// not a `<DropdownMenuItem>` somewhere, not a branch in the topbar. If a person adding
// "new expense claim" six months from now has to write JSX, this file is wrong and the
// fix belongs in this file rather than at their call site.
//
// # A short creation opens a pane; durable work has an address.
//
// This is the correction, and it is the whole shape of the file. The first version
// declared a `to:` — a screen — and the `+` navigated there and asked its pane to open.
// Arda, on seeing it: "+ ona basınca ilgili sayfaya atıyor normalde böyle olmaması lazım
// sayfası neden değişiyor anlamadım ben" — it throws him to the relevant page, and he
// does not understand why the page changes.
//
// Nor should he for a short, atomic act. A leave request or a receipt remains a pane so
// the page underneath survives it. Hiring is the deliberate exception: it is resumable,
// has several independently saved decisions and is handed between people. Treating that
// as a pane makes browser memory the case store. The declaration therefore admits exactly
// one of `opens` (an atomic pane) or `to` (a durable workspace), never both.
//
// # WHO MAY is a courtesy, in the server's own vocabulary
//
// `requires` is `destinations.ts`'s `Requirement`, checked through the same `permits`,
// against the same `ROLES` mirror that `lib/access.ts` keeps honest. One vocabulary, so
// "may I reach this screen" and "may I create this thing" cannot answer differently for
// the same role — which they would within a week if this file invented its own.
//
// It stays a courtesy and never the enforcement: riksu refuses the write regardless. What
// it buys is that an item somebody's token cannot reach is not OFFERED, because a menu
// entry that always refuses teaches somebody the product is broken and then that the
// whole menu is decorative. That is the rail's lesson, and it applies here unchanged.

import type { LucideIcon } from "lucide-react";
import {
  BriefcaseBusinessIcon,
  CalendarPlusIcon,
  CalendarRangeIcon,
  DoorOpenIcon,
  FileUpIcon,
  HandCoinsIcon,
  MegaphoneIcon,
  MessageSquarePlusIcon,
  ReceiptIcon,
  ReceiptTextIcon,
  UserPlusIcon,
} from "lucide-react";
import { type Held, holdsModule, type ModuleAccess, ROLES } from "../lib/access";
import type { MessageKey } from "../shell/i18n";
import type { CreateFlow } from "../ui/create-flow";
import { permits, type Requirement } from "./destinations";
import { lazyNamed } from "./lazy-screen";

const NewMessage = lazyNamed(() => import("./message-compose"), "NewMessage");
const NewServiceRequest = lazyNamed(() => import("./service-requests"), "NewServiceRequest");
const NewLeaveRequest = lazyNamed(() => import("./create-flows"), "NewLeaveRequest");
const NewCertifiedSickness = lazyNamed(() => import("./leave-history"), "NewCertifiedSickness");
const NewDepartureNotice = lazyNamed(() => import("./departure-notice"), "NewDepartureNotice");
const NewLeaving = lazyNamed(() => import("./create-flows"), "NewLeaving");
const NewPosition = lazyNamed(() => import("./setup"), "NewPosition");
const CreateSpend = lazyNamed(() => import("./expense-creates"), "CreateSpend");
const CreateClaim = lazyNamed(() => import("./expense-creates"), "CreateClaim");
const CreateAdvance = lazyNamed(() => import("./expense-creates"), "CreateAdvance");
const FileDocument = lazyNamed(() => import("./file-a-document"), "FileDocument");
const NewPayrollPeriod = lazyNamed(() => import("./payroll-create"), "NewPayrollPeriod");
const PublishPosting = lazyNamed(() => import("./recruiting"), "PublishPosting");

interface CreationBase {
  /**
   * WHAT IT CREATES — the words on the menu item and in the palette.
   *
   * Also the creation's identity, the way `to` is a destination's: it is unique by
   * construction (two entries creating the same thing is one entry) and it is what the
   * menu hands back when somebody picks one.
   */
  readonly what: MessageKey;
  readonly icon: LucideIcon;
  /** WHO MAY, in the same terms the rail uses and the server enforces. */
  readonly requires: Requirement;
  /** Which licensed product must be enabled, or shared infrastructure explicitly. */
  readonly module: ModuleAccess;
}

export type Creation = CreationBase &
  (
    | {
        /** An atomic flow mounted over the current page. */
        readonly opens: CreateFlow;
        readonly to?: never;
      }
    | {
        /** A resumable workspace whose address survives refresh, handoff and return. */
        readonly to: string;
        readonly opens?: never;
      }
  );

export type PaneCreation = Creation & { readonly opens: CreateFlow; readonly to?: never };

export function isPaneCreation(creation: Creation): creation is PaneCreation {
  return creation.opens !== undefined;
}

const EVERYONE: Requirement = { kind: "everyone" };

/**
 * Everything this product can create, in menu order.
 *
 * ORDER IS THE DECLARATION'S, not the roles'. A menu whose items move about depending on
 * what somebody holds is a menu people stop scanning — the same rule the rail follows,
 * and `reachable` there is filtered rather than sorted for exactly this reason.
 *
 * The everyday self-service creations come first because they are what most people open
 * this menu for; the administrative ones follow and most readers will never see them.
 *
 * THE EXPENSE MODULE DECLARES THREE, not one, and the split is the domain's rather than a
 * preference: a spend is a line on a claim riksu infers for you, a claim is the container
 * and opening one deliberately is how a NAMED employer gets charged, and an advance is
 * company money leaving the account before anything has been bought — two approval gates
 * rather than one, and its own module. Collapsing them into "new
 * expense" would put the avans back inside the claim, which is the error that whole
 * change exists to correct.
 *
 * The advance's surface is `/advance` NOW, its own module rather than a section of this
 * one — asked for three times — and this menu is where the three still sit together, which
 * is correct: a create menu is a list of ACTS and not a map of the rail.
 */
export const CREATIONS: readonly Creation[] = [
  {
    what: "create.message",
    module: { kind: "licensed", module: "messaging" },
    icon: MessageSquarePlusIcon,
    opens: NewMessage,
    requires: EVERYONE,
  },
  {
    what: "departureNotice.new",
    module: { kind: "licensed", module: "offboarding" },
    icon: DoorOpenIcon,
    opens: NewDepartureNotice,
    requires: EVERYONE,
  },
  {
    what: "serviceRequests.new",
    module: { kind: "licensed", module: "people" },
    icon: MessageSquarePlusIcon,
    opens: NewServiceRequest,
    requires: EVERYONE,
  },
  {
    what: "create.leaveRequest",
    module: { kind: "licensed", module: "leave" },
    icon: CalendarPlusIcon,
    opens: NewLeaveRequest,
    // Not role-gated, and that is a claim rather than a default: `riksu-authz` narrows a
    // leave request to the caller's own employment, so this is useful to somebody holding
    // nothing at all. The pane itself says so when there is no balance to spend against,
    // which is a different question from whether the caller holds a role.
    requires: EVERYONE,
  },
  {
    what: "create.certifiedSickness",
    module: { kind: "licensed", module: "leave" },
    icon: CalendarRangeIcon,
    opens: NewCertifiedSickness,
    requires: EVERYONE,
  },
  // RECORDING A SPEND is the everyday one and sits with the leave request: somebody is
  // standing there holding a receipt, and the amount is the only fact they have.
  {
    what: "create.expenseSpend",
    module: { kind: "licensed", module: "expense" },
    icon: ReceiptIcon,
    opens: CreateSpend,
    // Not role-gated. Opening a claim, putting a line on it and asking for it to be
    // approved are self-service BY DESIGN — they are how a worker gets their own money
    // back — and `riksu-authz` narrows each to the caller's own employment. The pane says
    // so when this account is nobody's employment record, which is not a role question.
    requires: EVERYONE,
  },
  {
    // THE CLAIM ITSELF, for a trip somebody is about to take rather than a receipt in
    // their hand — and the only way to charge a named employer when there are two.
    what: "create.expenseClaim",
    module: { kind: "licensed", module: "expense" },
    icon: ReceiptTextIcon,
    opens: CreateClaim,
    requires: EVERYONE,
  },
  {
    // THE AVANS. Company money that has not left the account yet: this opens two gates
    // and hands over nothing, and no endpoint in riksu hands over money directly. Asking
    // is self-service; it is RECORDING A HAND-BACK that takes `finance.admin`, and that
    // control lives on the advance surface rather than in this menu.
    what: "create.advance",
    module: { kind: "licensed", module: "advance" },
    icon: HandCoinsIcon,
    opens: CreateAdvance,
    requires: EVERYONE,
  },
  {
    // FILING A DOCUMENT, which sits with the self-service entries because for most readers
    // that is what it is: handing in your own rapor is the ordinary way one arrives, and
    // migration 0058 built the subject's exception precisely so HR does not upload somebody
    // else's sick note for them.
    //
    // NOT ROLE-GATED, and that is a claim rather than a default. `reserve_upload` takes a
    // filing role for almost every class AND names one exception that needs none — the
    // subject, filing evidence into their own file — so a requirement here would hide the
    // entry from exactly the population the exception exists for. The pane narrows to what
    // this caller may actually file and SAYS so when that is nothing, which is the honest
    // place for the answer: it is a question about the caller AND the class AND whose file,
    // and `Requirement` can express only the first.
    what: "create.document",
    module: { kind: "foundation" },
    icon: FileUpIcon,
    opens: FileDocument,
    requires: EVERYONE,
  },
  {
    // A JOB ADVERT, and it sits BEFORE the hire because that is the order of the arrow:
    // a company advertises, somebody applies, and only then is anybody employed.
    //
    // It is here because the product owner could not find it — *"İlanlar tarafında yeni ilan
    // girişi yapabileceğimiz bir buton falan yok"* — and the register at `/recruiting/postings`
    // gaining its own primary action is only half the answer. The palette is where somebody
    // looks for the thing missing from the menu, and publishing an advert was in neither.
    //
    // `ROLES.recruiting` and NOT `ROLES.recruitingSeats`: `publish_posting` takes
    // `RECRUITERS`, which is narrower than the set that may READ the hiring plan by the two
    // approval-chain roles. Offering it to a finance approver is a menu item riksu answers
    // 403 to — the defect this whole file's header is about.
    //
    // `PublishPosting` is self-sufficient with no `against`, which is what makes it a
    // `CreateFlow` at all: the shell hands a pane nothing, so the requisition is the pane's
    // own first question rather than something a screen passes in.
    what: "create.jobPosting",
    module: { kind: "licensed", module: "recruiting" },
    icon: MegaphoneIcon,
    opens: PublishPosting,
    requires: { kind: "role", anyOf: ROLES.recruiting },
  },
  {
    // A HIRE, and it is the first creation in this list that is a PERSON.
    //
    // It opens the administrative half, ahead of the leaving below it, because the two are
    // the ends of one lifecycle and this is the end somebody reaches first. Until it landed
    // riksu could read people that only a script could create.
    //
    // `ROLES.people` is the INTERSECTION of the two writes' own gates and that is deliberate.
    // `POST /v1/people` takes `PERSON_CREATORS` — `people.hr`, `payroll.admin` **and
    // `recruiting.admin`** — while the employment takes `PEOPLE_ADMINISTRATORS`, which is
    // the first two; `people.rs` says why in as many words: "Widening the shared constant
    // would have handed recruiting the hire as well." So a recruiting administrator may put
    // a candidate on file and may not employ one, and offering them THIS is offering a menu
    // item riksu answers 403 to on the second write, after the first has landed. Their own
    // surface is recruiting's rather than the hire's.
    //
    // THE REASON THAT USED TO CLOSE THAT SENTENCE WAS "that module is 9/9 headless", AND IT
    // HAS EXPIRED. `/recruiting` is five screens and the entry directly above is its second
    // creation. The RULE is untouched — the intersection is still right, and a recruiting
    // administrator still may not employ anybody — but the ground has moved: what a recruiter
    // is offered instead is now a real surface rather than a gap somebody was waiting on.
    //
    // What has NOT moved, and is the reason this is a narrowing rather than an oversight: a
    // recruiter can put a candidate on file through no screen in this product. `POST
    // /v1/people` takes `PERSON_CREATORS`, which includes `recruiting.admin`, and its only
    // call site is `Hire`, which chains an employment they may not write. That is a separate
    // gap and it is being closed separately — it is NOT a reason to widen this entry.
    what: "create.hire",
    module: { kind: "licensed", module: "people" },
    icon: UserPlusIcon,
    to: "/people/hires/new",
    requires: { kind: "role", anyOf: ROLES.people },
  },
  {
    // A LEAVING, and it is `offboarding.admin`'s or HR's. Offboarding is one of the three
    // surfaces with no self-service in them: every write there takes a role rather than a
    // per-endpoint judgement, so an ordinary employee offered this gets a 403 on submit.
    what: "create.leaving",
    module: { kind: "licensed", module: "offboarding" },
    icon: DoorOpenIcon,
    opens: NewLeaving,
    requires: { kind: "role", anyOf: ROLES.offboarding },
  },
  {
    // A POSITION, which is the setup step people come back to — a seat exists
    // independently of whoever sits in it, so creating one is ordinary company work
    // rather than a once-per-lifetime bootstrap the way a legal entity is.
    what: "create.position",
    module: { kind: "licensed", module: "organization" },
    icon: BriefcaseBusinessIcon,
    opens: NewPosition,
    requires: { kind: "role", anyOf: ROLES.organization },
  },
  {
    // A PAYROLL MONTH, which is the most REGULAR creation in this list — one per employer
    // per month, forever, which is exactly the shape a `+` exists for. A legal entity and
    // a job classification are deliberately absent from this menu because they are not
    // weekly work; this is the opposite case.
    //
    // `ROLES.payroll` and not `ROLES.people`: declaring the period is the first half of
    // the control that observes HR's own corrections, so offering it to `people.hr` would
    // be a menu item riksu answers 403 to.
    what: "create.payrollPeriod",
    module: { kind: "licensed", module: "payroll" },
    icon: CalendarRangeIcon,
    opens: NewPayrollPeriod,
    requires: { kind: "role", anyOf: ROLES.payroll },
  },
];

/**
 * Whether this caller may be offered a creation.
 *
 * It takes what the caller HOLDS rather than their roles, because `Requirement` can now ask
 * for a scope as well — and the two are one value precisely so a call site cannot hand the
 * roles to the scope check by writing them in the wrong order.
 *
 * No creation declares `andScope` today, and that is not a reason to narrow the parameter:
 * the vocabulary is `destinations.ts`' and this reads it, so a creation gated on a scope
 * tomorrow needs a line there and nothing here.
 */
export function canCreate(creation: Creation, held: Held | undefined): boolean {
  if (creation.module.kind === "licensed" && !holdsModule(held, creation.module.module)) {
    return false;
  }
  return permits(creation.requires, held);
}

/** The creations this caller may be offered, in declaration order. */
export function creatable(held: Held | undefined): Creation[] {
  return CREATIONS.filter((creation) => canCreate(creation, held));
}
